import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { SpinnerService } from 'src/app/common/services/spinner.service';
import { DataManagementService } from 'src/app/common/services/data-management.service';
import { Env } from '../../models';

@Component({
  selector: 'app-localization-sync',
  templateUrl: './localization-sync.component.html',
})
export class LocalizationSyncComponent implements OnInit, OnDestroy {
  env: Env;
  sourceTables: { name: string }[] | null = null;
  targetTables: { name: string }[] = [];
  error: string | null = null;
  inProgressMessage: string | null = null;
  status: any = null;
  private pollInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private router: Router,
    private dataManagementService: DataManagementService,
    private spinnerService: SpinnerService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private ngZone: NgZone,
  ) {}

  async ngOnInit(): Promise<void> {
    this.env = window.location.href.includes('dev.cms')
      ? 'dev'
      : window.location.href.includes('localhost')
      ? 'local'
      : window.location.href.includes('test.cms')
      ? 'test'
      : 'prod';

    if (this.env !== 'local') {
      this.router.navigate(['/home']);
      return;
    }

    await this.loader();
    await this.loadStatus();
  }

  async loader() {
    this.spinnerService.loadSpinner();
    this.error = null;
    this.targetTables = [];

    try {
      const response = await this.dataManagementService.listRemoteLocalizationTables();
      if (response.success && response.tables?.length > 0) {
        this.sourceTables = response.tables.map((table: string) => ({ name: table }));
        this.cdr.markForCheck();
      } else if (response.error) {
        this.error = response.error;
      }
    } catch (error: any) {
      this.error = error?.error?.error || error?.message || 'Failed to list remote localization tables.';
    } finally {
      this.spinnerService.endSpinner();
    }
  }

  async syncToLocal() {
    this.inProgressMessage = null;
    if (this.targetTables.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'No tables selected',
        detail: 'You must select at least one localization table to sync to local',
        life: 5000,
      });
      return;
    }

    try {
      const tables = this.targetTables.map((table) => table.name);
      const response = await this.dataManagementService.syncLocalizationTablesToLocal(tables);
      if (response.success) {
        this.messageService.add({
          severity: 'info',
          summary: 'Localization sync started',
          detail: 'Copying selected tables from production Aurora into local MySQL.',
        });
        await this.loadStatus();
        this.startPolling();
      } else if (response.warning) {
        this.inProgressMessage = response.warning;
        this.messageService.add({ severity: 'warn', summary: 'Warning', detail: response.warning, life: 5000 });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: response.error || 'There was an error starting the localization sync',
          life: 5000,
        });
      }
    } catch (error: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: error?.error?.error || 'There was an error starting the localization sync',
        life: 5000,
      });
    }
  }

  private async loadStatus() {
    try {
      this.status = await this.dataManagementService.getLocalizationLocalSyncStatus();
      if (this.status?.inProgress) {
        this.startPolling();
      }
    } catch {
      // ignore
    }
  }

  private startPolling() {
    if (this.pollInterval) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.pollInterval = setInterval(async () => {
        try {
          const status = await this.dataManagementService.getLocalizationLocalSyncStatus();
          this.ngZone.run(() => {
            this.status = status;
            if (!status?.inProgress) {
              this.stopPolling();
              if (status?.lastStatus === 'success') {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Localization sync complete',
                  detail: status.lastMessage || 'Selected tables were copied to local MySQL.',
                });
              } else if (status?.lastStatus === 'failed') {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Localization sync failed',
                  detail: status.lastMessage || 'Sync failed.',
                });
              }
            }
          });
        } catch {
          // ignore poll errors
        }
      }, 2000);
    });
  }

  private stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  ngOnDestroy() {
    this.stopPolling();
  }
}
